import React, { useEffect, useState } from 'react';
import siteService from '../services/siteService';
import { debouncedGeocode, debouncedGeocodeMulti, debouncedGeocodeMultiNotable, debouncedGeocodeMultiSmart } from '../services/geocodeClient';
import { useI18n } from '../i18n/I18nProvider';
import authService from '../services/authService';

/*
  Sites Page
  - Lists existing sites (site_code, name, address, phone, coordinates)
  - Allows admin & manager to create new sites (site_code required, name optional)
  - Future enhancements: edit, delete/disable, map view, attach bins summary
*/

const SitesPage = () => {
  const { t } = useI18n();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [newSite, setNewSite] = useState({
    site_code: '',
    site_name: '',
    address: '',
    phone_number: '',
    latitude: '',
    longitude: '',
  });
  // Inline edit state
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({ site_code:'', site_name:'', address:'', phone_number:'', latitude:'', longitude:'' });
  const [editError, setEditError] = useState('');
  const [liveGeo, setLiveGeo] = useState(null); // first suggestion preview
  const [geoStatus, setGeoStatus] = useState('idle'); // idle|loading|ok|fail
  const [createSuggestions, setCreateSuggestions] = useState([]); // suggestions while creating
  const [editSuggestions, setEditSuggestions] = useState([]); // suggestions while editing
  // After a suggestion is chosen for create form, suppress async geocode responses from repopulating the list
  const [suppressCreateSuggestions, setSuppressCreateSuggestions] = useState(false);
  // Suppress late async geocode results after selecting a suggestion in edit mode
  const [suppressEditSuggestions, setSuppressEditSuggestions] = useState(false);

  const currentUser = authService.getCurrentUser();
  const role = currentUser?.role || (currentUser?.token ? authService.decodeJwt(currentUser.token)?.user?.role : null);
  const canCreate = role === 'admin' || role === 'manager';

  const fetchSites = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await siteService.getSites();
      setSites(data || []);
    } catch (e) {
      const msg = e.response?.data?.msg || e.response?.data?.message || e.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSites(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewSite(s => ({ ...s, [name]: value }));
  };

  const resetForm = () => {
    setNewSite({ site_code: '', site_name: '', address: '', phone_number: '', latitude: '', longitude: '' });
    setCreateError('');
  };

  const beginEdit = (site, e) => {
    if(e) e.stopPropagation();
    setEditingId(site.site_id);
    const lat = site.location?.coordinates ? site.location.coordinates[1] : '';
    const lng = site.location?.coordinates ? site.location.coordinates[0] : '';
    setEditValues({
      site_code: site.site_code || '',
      site_name: site.site_name || '',
      address: site.address || '',
      phone_number: site.phone_number || '',
      latitude: lat !== null && lat !== undefined ? String(lat) : '',
      longitude: lng !== null && lng !== undefined ? String(lng) : '',
    });
    setEditError('');
  };
  const cancelEdit = (e) => { if(e) e.stopPropagation(); setEditingId(null); setEditError(''); };
  const onEditFieldChange = (field,val) => setEditValues(v => ({ ...v, [field]: val }));
  // Address change live geocode for edit (allow suggestions even if coords already filled)
  useEffect(()=>{
  if(!editingId){ setLiveGeo(null); setEditSuggestions([]); return; }
  if(!editValues.address || editValues.address.trim().length < 3){ setLiveGeo(null); setEditSuggestions([]); setGeoStatus('idle'); return; }
    // Do NOT block when latitude/longitude already filled; still show alternative suggestions
    let cancelled = false;
    setGeoStatus('loading');
    debouncedGeocodeMulti(editValues.address.trim()).then(res => {
      if(cancelled) return;
      if(suppressEditSuggestions) return; // ignore late response after user already chose one
      if(Array.isArray(res) && res.length){
        setEditSuggestions(res);
        setLiveGeo({ lat: res[0].latitude, lon: res[0].longitude });
        setGeoStatus('ok');
      } else { setLiveGeo(null); setEditSuggestions([]); setGeoStatus('fail'); }
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId, editValues.address]);
  const saveEdit = async (site, e) => {
    if(e) e.stopPropagation();
    if(!editingId) return;
    setEditError('');
    const payload = {
      site_code: editValues.site_code.trim(),
      site_name: editValues.site_name.trim(),
      address: editValues.address.trim() || undefined,
      phone_number: editValues.phone_number.trim() || undefined,
    };
    const latNum = parseFloat(editValues.latitude.replace(',','.'));
    const lonNum = parseFloat(editValues.longitude.replace(',','.'));
    if(!isNaN(latNum) && !isNaN(lonNum)) { payload.latitude = latNum; payload.longitude = lonNum; }
    try {
      const updated = await siteService.updateSite(site.site_id, payload);
      setSites(prev => prev.map(s => s.site_id === site.site_id ? { ...s, ...updated } : s));
      setEditingId(null);
    } catch(err){
      const apiMsg = err.response?.data?.msg || err.message || 'Save failed';
      setEditError(apiMsg);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (creating) return;
    if (!newSite.site_code.trim()) {
      setCreateError(t('sites.validationCodeRequired') || 'Code required'); return;
    }
    setCreating(true);
    setCreateError('');
    try {
      const payload = {
        site_code: newSite.site_code.trim(),
        site_name: newSite.site_name.trim(),
        address: newSite.address.trim() || undefined,
        phone_number: newSite.phone_number.trim() || undefined,
      };
      const lat = parseFloat(newSite.latitude.replace(',', '.'));
      const lng = parseFloat(newSite.longitude.replace(',', '.'));
      if (!isNaN(lat) && !isNaN(lng)) { payload.latitude = lat; payload.longitude = lng; }
      await siteService.createSite(payload);
      resetForm();
      fetchSites();
    } catch (err) {
      const apiMsg = err.response?.data?.msg || err.response?.data?.message || err.message;
      setCreateError(apiMsg);
    } finally { setCreating(false); }
  };

  return (
    <div className="sb-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '12px 0 8px' }}>
        <h4 style={{ margin: 0 }}>{t('sites.title')}</h4>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn" onClick={fetchSites} disabled={loading}>{t('common.refresh') || 'Refresh'}</button>
        </div>
      </div>
      {error && <div className="alert alert-danger" style={{ marginBottom: 12 }}>{error}</div>}
      {canCreate && (
        <form onSubmit={handleCreate} style={{ margin: '8px 0 18px', background: '#fafafa', border: '1px solid #e0e0e0', padding: '10px 12px', borderRadius: 6, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 120 }}>
            <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('sites.fields.code')}</label>
            <input className="form-control" name="site_code" value={newSite.site_code} onChange={handleChange} disabled={creating} placeholder={t('sites.placeholders.code')} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 160 }}>
            <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('sites.fields.name')}</label>
            <input className="form-control" name="site_name" value={newSite.site_name} onChange={handleChange} disabled={creating} placeholder={t('sites.placeholders.name')} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 200, position:'relative' }}>
            <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('sites.fields.address')}</label>
            <input className="form-control" name="address" value={newSite.address} onChange={e=> {
              const val = e.target.value; setNewSite(s => ({ ...s, address: val }));
              // User started typing again -> allow suggestions to appear
              if(suppressCreateSuggestions) setSuppressCreateSuggestions(false);
              if(val.trim().length < 3){ setCreateSuggestions([]); return; }
              // Smart: notable először, ha nincs vagy tipikus cím minta -> teljes találat lista
              debouncedGeocodeMultiSmart(val.trim()).then(res => {
                if(suppressCreateSuggestions) return; // ignore late async response after selection
                if(Array.isArray(res) && res.length) setCreateSuggestions(res); else setCreateSuggestions([]);
              });
            }} disabled={creating} placeholder={t('sites.placeholders.address')} />
            {createSuggestions.length === 0 && newSite.address.trim().length >=3 && <div className="geo-spinner-wrapper"><div className="geo-spinner" /></div>}
            {createSuggestions.length > 0 && (
              <div style={{ position:'absolute', top:'100%', left:0, right:0, marginTop:4, fontSize:10, background:'#fff', border:'1px solid #bcd3c7', padding:'4px 6px', borderRadius:4, maxHeight:160, overflowY:'auto', zIndex:20 }}>
                <div style={{ fontWeight:600, fontSize:10, marginBottom:4 }}>{t('common.suggestions') || 'Javaslatok'}</div>
                {createSuggestions.map((sug,i)=>{
                  const comps = sug.components || {};
                  const formatted = [comps.postcode, comps.city, comps.street, comps.number].filter(Boolean).join(' ');
                  return (
                    <div
                      key={i}
                      style={{ padding:'3px 2px', cursor:'pointer', borderBottom:'1px solid #eee' }}
                      onClick={()=>{
                        // Reconstruct address; fallback to existing address text if components incomplete
                        setNewSite(s => ({
                          ...s,
                          address: formatted || sug.display || s.address,
                          latitude: String(sug.latitude),
                          longitude: String(sug.longitude)
                        }));
                        setSuppressCreateSuggestions(true);
                        setCreateSuggestions([]); // clear immediately; late promises ignored via suppress flag
                      }}
                    >
                      <strong>{sug.latitude.toFixed(5)}, {sug.longitude.toFixed(5)}</strong>
                      {sug._notable && <span title={t('geocode.notableHint') || 'Nevezetes találat'} className="geo-notable-dot" />}
                      <br/>
                      <span style={{ opacity:.75 }}>{sug.display}</span>
                      {formatted && <div style={{ fontSize:9, opacity:.6, marginTop:2 }}>{formatted}</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 140 }}>
            <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('sites.fields.phone')}</label>
            <input className="form-control" name="phone_number" value={newSite.phone_number} onChange={handleChange} disabled={creating} placeholder={t('sites.placeholders.phone')} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', width: 110 }}>
            <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('sites.fields.latitude')}</label>
            <input className="form-control" name="latitude" value={newSite.latitude} onChange={handleChange} disabled={creating} placeholder={t('sites.placeholders.latitude')} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', width: 110 }}>
            <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('sites.fields.longitude')}</label>
            <input className="form-control" name="longitude" value={newSite.longitude} onChange={handleChange} disabled={creating} placeholder={t('sites.placeholders.longitude')} />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="submit" className="btn btn-primary" disabled={creating} style={{ padding: '6px 14px' }}>{creating ? (t('common.saving') || '...') : (t('sites.form.create') || 'Create')}</button>
            {createError && <span style={{ color: '#c62828', fontSize: 12 }}>{createError}</span>}
          </div>
        </form>
      )}
      {loading && <div style={{ fontSize: 13 }}>{t('common.loading') || 'Loading...'}</div>}
      {!loading && sites.length === 0 && <div style={{ fontSize: 13, opacity: .7 }}>{t('sites.empty')}</div>}
      {sites.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: 'left', background: '#f5f5f5' }}>
              <th style={{ padding: '8px 10px', borderBottom: '1px solid #ddd' }}>{'ID'}</th>
              <th style={{ padding: '8px 10px', borderBottom: '1px solid #ddd' }}>{t('sites.fields.name')}</th>
              <th style={{ padding: '8px 10px', borderBottom: '1px solid #ddd' }}>{t('sites.fields.address')}</th>
              <th style={{ padding: '8px 10px', borderBottom: '1px solid #ddd' }}>{t('sites.fields.phone')}</th>
              <th style={{ padding: '8px 10px', borderBottom: '1px solid #ddd' }}>{t('sites.fields.coordinates')}</th>
              { role === 'admin' && <th style={{ padding: '8px 10px', borderBottom: '1px solid #ddd', minWidth:160 }}>{t('users.actions') || 'Actions'}</th> }
            </tr>
          </thead>
          <tbody>
            {sites.map(s => {
              const lat = s.latitude != null ? s.latitude : (s.location?.coordinates ? s.location.coordinates[1] : null);
              const lng = s.longitude != null ? s.longitude : (s.location?.coordinates ? s.location.coordinates[0] : null);
              const isEditing = editingId === s.site_id;
              return (
                <tr key={s.site_id} style={{ borderBottom: '1px solid #eee', background: isEditing ? '#f9f9fb' : undefined }}>
                  <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>
                    {isEditing ? (
                      <input style={{ width:'100%' }} value={editValues.site_code} onChange={e=> onEditFieldChange('site_code', e.target.value)} />
                    ) : s.site_code}
                  </td>
                  <td style={{ padding: '6px 10px' }}>
                    {isEditing ? (
                      <input style={{ width:'100%' }} value={editValues.site_name} onChange={e=> onEditFieldChange('site_name', e.target.value)} />
                    ) : s.site_name}
                  </td>
                  <td style={{ padding: '6px 10px' }}>
                    {isEditing ? (
                      <div style={{ display:'flex', flexDirection:'column', gap:4, position:'relative' }}>
                        <input style={{ width:'100%' }} value={editValues.address} onChange={e=> { if(suppressEditSuggestions) setSuppressEditSuggestions(false); onEditFieldChange('address', e.target.value); }} />
                        {geoStatus === 'loading' && <div className="geo-spinner-wrapper" style={{ top:2, right:2 }}><div className="geo-spinner" /></div>}
                        {/* Loading text removed to prevent height jumping */}
                        {editSuggestions.length > 0 && geoStatus !== 'loading' && (
                          <div style={{ position:'absolute', top:'100%', left:0, right:0, marginTop:4, fontSize:10, background:'#fff', border:'1px solid #bcd3c7', padding:'4px 6px', borderRadius:4, maxHeight:160, overflowY:'auto', zIndex:25 }}>
                            <div style={{ fontWeight:600, fontSize:10, marginBottom:4 }}>{t('common.suggestions') || 'Javaslatok'}</div>
                            {editSuggestions.map((sug,i)=>{
                              const comps = sug.components || {};
                              const formatted = [comps.postcode, comps.city, comps.street, comps.number].filter(Boolean).join(' ');
                              return (
                                <div
                                  key={i}
                                  style={{ padding:'3px 2px', cursor:'pointer', borderBottom:'1px solid #eee' }}
                                  onClick={()=>{
                                    onEditFieldChange('latitude', String(sug.latitude));
                                    onEditFieldChange('longitude', String(sug.longitude));
                                    onEditFieldChange('address', formatted || sug.display || editValues.address);
                                    setLiveGeo({lat:sug.latitude, lon:sug.longitude});
                                    setSuppressEditSuggestions(true);
                                    setEditSuggestions([]);
                                  }}
                                >
                                  <strong>{sug.latitude.toFixed(5)}, {sug.longitude.toFixed(5)}</strong>
                                  {sug._notable && <span title={t('geocode.notableHint') || 'Nevezetes találat'} className="geo-notable-dot" />}
                                  <br/>
                                  <span style={{ opacity:.75 }}>{sug.display}</span>
                                  {formatted && <div style={{ fontSize:9, opacity:.55, marginTop:2 }}>{formatted}</div>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {/* Removed fail message to avoid layout jump */}
                      </div>
                    ) : (s.address || '—')}
                  </td>
                  <td style={{ padding: '6px 10px' }}>
                    {isEditing ? (
                      <input style={{ width:'100%' }} value={editValues.phone_number} onChange={e=> onEditFieldChange('phone_number', e.target.value)} />
                    ) : (s.phone_number || '—')}
                  </td>
                  <td style={{ padding: '6px 10px' }}>
                    {isEditing ? (
                      <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                        <input style={{ width:'50%' }} placeholder={t('sites.fields.latitude')} value={editValues.latitude} onChange={e=> onEditFieldChange('latitude', e.target.value)} />
                        <input style={{ width:'50%' }} placeholder={t('sites.fields.longitude')} value={editValues.longitude} onChange={e=> onEditFieldChange('longitude', e.target.value)} />
                      </div>
                    ) : ((lat != null && lng != null) ? `${lat}, ${lng}` : '—')}
                  </td>
                  { role === 'admin' && (
                    <td style={{ padding: '6px 10px' }}>
                      {!isEditing && <button type="button" className="btn btn-sm" style={{ background:'#1565c0', color:'#fff', fontSize:11, padding:'4px 10px', border:'none', borderRadius:4, marginRight:4 }} onClick={(e)=> beginEdit(s,e)}>{t('users.edit') || t('common.edit') || 'Edit'}</button>}
                      {isEditing && (
                        <>
                          <button type="button" className="btn btn-sm" style={{ background:'#2e7d32', color:'#fff', fontSize:11, padding:'4px 10px', border:'none', borderRadius:4, marginRight:4 }} onClick={(e)=> saveEdit(s,e)}>{t('users.save') || t('common.save') || 'Save'}</button>
                          <button type="button" className="btn btn-sm" style={{ background:'#757575', color:'#fff', fontSize:11, padding:'4px 10px', border:'none', borderRadius:4 }} onClick={cancelEdit}>{t('users.cancel') || t('common.cancel') || 'Cancel'}</button>
                          {editError && <div style={{ fontSize:10, color:'#c62828', marginTop:4 }}>{editError}</div>}
                        </>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default SitesPage;
